/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { get, startsWith } from 'lodash';

export const defaultPresenter: string = 'Default';

// Keep grouped by presenter type and sorted alphabetically within the group
export const attributeToPresenter: { [key: string]: string } = {
  // TODO: Handle when adding CAs
  '_Dynamic_:riskScore5': 'Default', // On Risk, float presenter
  '_Dynamic_:riskScore6': 'Default', // On Risk, float presenter
  //

  // TODO: consolidate into Select presenter (currently named Dropdown presenter)
  kind: 'Kind',
  means: 'Means',

  // TODO: consolidate into Checkbox presenter (currently named Boolean presenter)
  isWeighted: 'Boolean',
  fraudRelated: 'Boolean',
  syncExternalAttachments: 'Boolean',
  syncExternalComments: 'Boolean',
  internal: 'AuditType',
  keyControl: 'Significance',
  watchers: 'Watching',

  // TODO: rename Attachments presenter to Documents presenter
  documents: 'Attachments',

  // TODO: consolidate into Date presenter
  autoNagDate: 'Date',
  resentAt: 'Date',
  respondedAt: 'Date',
  sentAt: 'Date',
  startDate: 'Date',
  createdAt: 'DateTime',
  updatedAt: 'DateTime',
  reportStartDate: 'DateTime',
  reportEndDate: 'DateTime',
  endDate: 'EndDate',

  // TODO: rename Default presenter to Text presenter
  action: 'Default',
  autoNagRecurrence: 'Default',
  company: 'Default',
  conclusion: 'Default',
  count: 'Default',
  design: 'Default',
  eventId: 'Default',
  networkZone: 'Default',
  objectId: 'Default',
  objectType: 'Default',
  operationally: 'Default',
  publicId: 'Default',
  resentCounter: 'Default',
  salutations: 'Default',
  settings: 'Default',
  slug: 'Default',
  subject: 'Default',
  surveyTakerEmail: 'Default',
  surveyTakerName: 'Default',
  userRole: 'Default',
  verifyFrequency: 'Default',

  score: 'Float',
  highRiskThreshold: 'Float',
  midRiskThreshold: 'Float',

  // TODO: consolidate to Link presenter
  referenceUrl: 'Link',
  url: 'Link',
  title: 'Title',
  titleCode: 'Title', // Colgate-specific attribute
  name: 'Title',
  email: 'Title',
  emailTitle: 'Title',

  assessments: 'Object',
  audit: 'Object',
  control: 'Object',
  fetchers: 'Object',
  program: 'Object',
  relevantAssessments: 'Object',
  relevantAudits: 'Object',
  relevantControls: 'Object',
  relevantPrograms: 'Object',
  mappingFrom: 'Object',
  modifiedObject: 'Object',
  mappingTo: 'Object',

  // TODO: rename RelatedObjects presenter to RelatedObject presenter
  relatedObjects: 'RelatedObjects',

  // TODO: consolidate into Person presenter
  auditors: 'Person',
  auditManagers: 'Person',
  contact: 'Person',
  owner: 'Person',
  owners: 'Person',
  principalAssessor: 'Person',
  programEditors: 'Person',
  programOwners: 'Person',
  programReaders: 'Person',
  relatedAssessors: 'Person',
  relatedAssignees: 'Person',
  relatedCreators: 'Person',
  relatedRequesters: 'Person',
  relatedVerifiers: 'Person',
  secondaryAssessor: 'Person',
  secondaryContact: 'Person',
  modifiedBy: 'Person',
  people: 'Person',
  reviewers: 'Reviewers',
  // TODO: replace with Group presenter as Access Controls effort progresses
  ownersGroup: 'Group',

  surveyResponses: 'QuestionnaireResponses',

  recurrenceInfo: 'Recurrence',

  auditFirm: 'RichText',
  description: 'RichText',
  notes: 'RichText',
  test: 'RichText',
  testPlan: 'RichText',
  emailMessage: 'RichText',
  emailGreeting: 'RichText',

  rating: 'Rating',
  // TODO: Integrate RiskRating presenter into Rating with reversed colors for high, ...
  riskRating: 'RiskRating',

  // TODO: Consolidate into MultiSelect presenter
  status: 'Status',
  tags: 'Tags',
};

export const customAttributeToPresenter: { [key: string]: string } = {
  TEXT: 'Default',
  RICH_TEXT: 'RichText',
  DATE: 'Date',
  INT: 'Default',
  FLOAT: 'Default',
  CHECKBOX: 'Boolean',
  MULTI: 'Dropdown',
  MAP_PERSON: 'Person',
};

export const objTypeAttributeToPresenter: { [objectType: string]: string } = {
  SurveyTemplate: {
    title: 'SurveyTemplateTitle',
  },
  SurveyResponse: {
    title: 'SurveyResponseTitle',
    subject: 'SurveyResponseSubject',
  },
};

export function getCellPresenter(objectType: string, attribute: string, customAttributeType?: string): string {
  const isCustomAttribute: boolean = !!customAttributeType;
  const isMappedAttribute: (a: string) => boolean = a => startsWith(a, 'mappings.');

  if (isCustomAttribute) return customAttributeToPresenter[customAttributeType as string];
  if (isMappedAttribute(attribute)) return 'MappedObjects';

  if (objTypeAttributeToPresenter[objectType] && objTypeAttributeToPresenter[objectType][attribute]) {
    return objTypeAttributeToPresenter[objectType][attribute];
  }

  if (!attributeToPresenter[attribute]) {
    // eslint-disable-next-line no-console
    console.error(`Unable to find presenter for attribute: ${attribute}`);
  }

  return get(attributeToPresenter, attribute);
}
